/**
 * Replaces the report's authored form sections with /demo dummy data.
 *
 * Only Personal Details, Households, Rental Status, and Employment are
 * rewritten. AI, scores, credit, bank, and identity photos stay on the
 * fixture. Keep in sync with `DEMO_STEP_DATA` in `app/routes/demo.tsx`.
 */
import type { CombinedReportData } from '#app/utils/application-detail-v2/combined-types'
import type {
	ApplicationDetailData,
	ParsedStepData,
} from '#app/utils/application-detail-v2/types'

type OverlayReport =
	| { kind: 'detail'; data: ApplicationDetailData }
	| { kind: 'combined'; data: CombinedReportData }

type PropertyStep = NonNullable<ParsedStepData['property']>
type Adult = NonNullable<PropertyStep['adults']>[number]
type Guarantor = NonNullable<PropertyStep['guarantor']>

export type OverlayKind = 'standard' | 'student' | 'guarantor'

function streetAddress(
	street: string,
	city: string,
	province: string,
	postal: string,
): string {
	const locality = [city, province].filter(Boolean).join(', ')
	return [street, locality, postal].filter(Boolean).join(', ')
}

const DEMO_ADULTS: Adult[] = [
	{
		id: 'adult-1',
		firstName: 'John',
		lastName: 'Carter',
		name: 'John Carter',
		email: 'john.carter@truetenant.ai',
		phone: '+14169671111',
		phoneCountryCode: 'CA',
		dateOfBirth: '1992-12-31',
		isPrimary: true,
	},
	{
		id: 'adult-2',
		firstName: 'Sarah',
		lastName: 'Carter',
		name: 'Sarah Carter',
		email: 'sarah.carter@truetenant.ai',
		phone: '+14165550199',
		phoneCountryCode: 'CA',
		dateOfBirth: '1995-06-18',
		relationship: 'Partner',
	},
]

const DEMO_STUDENT_ADULT: Adult = {
	...DEMO_ADULTS[0],
	dateOfBirth: '2004-09-12',
	gender: 'Male',
	schoolProgram:
		'University of Toronto Mississauga, Business, Commerce And Management',
	studyLevelAndGraduation: "Undergraduate (Bachelor's), April 2028",
}

const DEMO_GUARANTOR: Guarantor = {
	id: 'student-guarantor-1',
	firstName: 'Robert',
	lastName: 'Carter',
	name: 'Robert Carter',
	email: 'robert.carter@truetenant.ai',
	phone: '+19053333333',
	phoneCountryCode: 'CA',
	relationship: 'Parent',
	dateOfBirth: '1992-12-31',
	linkedOccupantIds: ['adult-1'],
}

const DEMO_GUARANTOR_ADULT: Adult = {
	id: 'guarantor-self',
	firstName: 'Robert',
	lastName: 'Carter',
	name: 'Robert Carter',
	email: 'robert.carter@truetenant.ai',
	phone: '+19053333333',
	phoneCountryCode: 'CA',
	relationship: 'Parent',
	dateOfBirth: '1992-12-31',
}

const DEMO_CHILDREN = [{ id: 'child-1', dateOfBirth: '2020-03-13' }]

const DEMO_PETS = [
	{
		id: 'pet-1',
		petType: 'dog',
		weight: '10-30 lbs',
		breed: 'Labrador',
	},
]

const DEMO_VEHICLES = [
	{
		id: 'vehicle-1',
		vehicleType: 'car',
		registration: 'CABC123',
		makeModel: 'Toyota Camry',
		color: 'White',
	},
]

/** Currently-renting answers from the demo form (review dataset). */
const DEMO_RENTAL: NonNullable<ParsedStepData['rental']> = {
	rentalStatus: 'currently-renting',
	currentLandlordName: 'Michael Thompson',
	currentLandlordPhone: '+14163334444',
	currentLandlordPhoneCountryCode: 'CA',
	currentLandlordEmail: 'michaellandlord@truetenant.ai',
	currentAddress: streetAddress(
		'55 Harbour Square',
		'Toronto',
		'ON',
		'M5J 2L1',
	),
	currentAddressCity: 'Toronto',
	currentAddressProvince: 'ON',
	currentAddressPostalCode: 'M5J 2L1',
	currentRent: '3200',
	currentPaymentMethod: 'Credit Card',
	currentYearsLived: '3 Years 4 Months',
	previousLandlordName: 'Jennifer Lewis',
	previousLandlordPhone: '+16475550123',
	previousLandlordPhoneCountryCode: 'CA',
	previousLandlordEmail: 'jenniferlandlord@truetenant.ai',
	previousAddress: streetAddress(
		'100 Queens Quay E',
		'Toronto',
		'ON',
		'M5E 1V3',
	),
	previousAddressCity: 'Toronto',
	previousAddressProvince: 'ON',
	previousAddressPostalCode: 'M5E 1V3',
	previousRent: '2600',
	previousPaymentMethod: 'Credit Card',
	previousMoveInDate: '2018-12-01',
	previousMoveOutDate: '2022-02-01',
	previousDurationLived: '3 Years 2 Months',
	receivedN4: '',
	receivedL1: '',
	everEvicted: 'no',
	everEvictedExplanation: '',
	everConvictedFelony: 'no',
	everConvictedFelonyExplanation: '',
	doesSmoke: 'no',
	newToCountry: 'no',
	immigrationStatus: '',
	currentAddressFirstRenter: '',
	currentLivingSituation: '',
	yearsLivedAtAddress: '',
	immigrationStatusFirstRenter: '',
	rentingRoom: 'no',
	notOnLease: 'no',
}

const DEMO_EMPLOYMENT: NonNullable<ParsedStepData['employment']> = {
	employmentStatus: 'currently-employed',
	companyName: 'TrueTenant',
	occupation: 'Software Engineer',
	annualIncome: '150000',
	companyPhone: '+14373334444',
	companyPhoneCountryCode: 'CA',
	workAddress: streetAddress('250 University Ave', 'Toronto', 'ON', 'M5H 3E5'),
	workAddressCity: 'Toronto',
	workAddressProvince: 'ON',
	workAddressPostalCode: 'M5H 3E5',
	yearsOfEmployment: '4 Years',
	employmentType: 'Permanent',
	maternityLeave: 'no',
	governmentAssistance: '',
	immigrationStatus: 'Canadian Citizen',
	immigrationStatusExpiration: '2028-08-31',
	additionalEmployments: [
		{
			companyName: 'Nova Retail Group',
			occupation: 'Sales Associate',
			annualIncome: '28000',
			companyPhone: '+14165550177',
			workAddress: streetAddress('220 Yonge St', 'Toronto', 'ON', 'M5B 2H1'),
			yearsOfEmployment: '1 Year',
			employmentType: 'Part-Time',
			sourceOfIncome: '',
		},
	],
	previousCompanyName: '',
	previousOccupation: '',
	previousAnnualIncome: '',
	previousCompanyPhone: '',
	previousWorkAddress: '',
	previousYearsOfEmployment: '',
	previousEmploymentStartDate: '',
	previousEmploymentEndDate: '',
	previousEmploymentType: '',
	previousSourceOfIncome: '',
	sourceOfIncome: '',
	noHistoryAnnualIncome: '',
}

const DEMO_STUDENT_EMPLOYMENT: NonNullable<ParsedStepData['employment']> = {
	...DEMO_EMPLOYMENT,
	maternityLeave: '',
	receivingStudentFinancialAssistance: 'no',
	studentFinancialAssistance: [
		{
			id: 'demo-aid-1',
			type: 'Scholarship',
			grossIncome: '8000',
			frequency: 'Yearly',
		},
	],
	receivingParentalAssistance: 'no',
	parentalAssistanceSource: 'Parental Assistance',
	parentalAssistanceGrossAmount: '1500',
	parentalAssistanceFrequency: 'Monthly',
}

function overlayKindFor(variant: string): OverlayKind {
	if (variant === 'haddad-karim' || variant === 'bennett-margaret') {
		return 'guarantor'
	}
	if (variant.startsWith('bennett')) return 'student'
	return 'standard'
}

function formPerson(kind: OverlayKind): {
	name: string
	email: string
	phone: string
} {
	if (kind === 'guarantor') {
		return {
			name: 'Robert Carter',
			email: 'robert.carter@truetenant.ai',
			phone: '+19053333333',
		}
	}
	return {
		name: 'John Carter',
		email: 'john.carter@truetenant.ai',
		phone: '+14169671111',
	}
}

/** Keep the listing (unit / rent / address); replace occupancy from the form. */
export function overlayPropertyStep(
	property: PropertyStep | undefined,
	kind: OverlayKind,
): PropertyStep {
	const {
		adults: _adults,
		children: _children,
		pets: _pets,
		vehicles: _vehicles,
		guarantor: _guarantor,
		guarantors: _guarantors,
		nextOfKin: _nextOfKin,
		...listing
	} = (property ?? {}) as PropertyStep

	if (kind === 'guarantor') {
		return {
			...listing,
			adults: [DEMO_GUARANTOR_ADULT],
			children: [],
			pets: [],
			vehicles: [],
			hasGuarantor: 'no',
			hasPet: 'no',
			hasVehicle: 'no',
			numAdults: '1',
			numChildren: '0',
			totalPets: '0',
			totalVehicles: '0',
			totalOccupants: '1',
		}
	}

	if (kind === 'student') {
		return {
			...listing,
			isStudentHousing: true,
			adults: [DEMO_STUDENT_ADULT],
			children: [],
			pets: DEMO_PETS,
			vehicles: DEMO_VEHICLES,
			hasGuarantor: 'yes',
			guarantor: DEMO_GUARANTOR,
			guarantors: [DEMO_GUARANTOR],
			hasPet: 'yes',
			hasVehicle: 'yes',
			numAdults: '1',
			numChildren: '0',
			totalPets: '1',
			totalVehicles: '1',
			totalOccupants: '1',
		}
	}

	return {
		...listing,
		adults: DEMO_ADULTS,
		children: DEMO_CHILDREN,
		pets: DEMO_PETS,
		vehicles: DEMO_VEHICLES,
		hasGuarantor: 'yes',
		guarantor: DEMO_GUARANTOR,
		hasPet: 'yes',
		hasVehicle: 'yes',
		numAdults: '2',
		numChildren: '1',
		totalPets: '1',
		totalVehicles: '1',
		totalOccupants: '3',
	}
}

function overlayDetail(
	data: ApplicationDetailData,
	kind: OverlayKind,
): ApplicationDetailData {
	const person = formPerson(kind)
	return {
		...data,
		application: {
			...data.application,
			applicantName: person.name,
			applicantEmail: person.email,
			applicantPhone: person.phone,
		},
		applicationCategory:
			kind === 'student' ? 'student_housing' : data.applicationCategory,
		steps: {
			...data.steps,
			property: overlayPropertyStep(data.steps.property, kind),
			rental: DEMO_RENTAL,
			employment:
				kind === 'student' ? DEMO_STUDENT_EMPLOYMENT : DEMO_EMPLOYMENT,
		},
	}
}

function overlayCombined(
	data: CombinedReportData,
	kind: OverlayKind,
): CombinedReportData {
	const propertyKind = kind === 'guarantor' ? 'standard' : kind
	return {
		...data,
		applicationCategory:
			kind === 'student' ? 'student_housing' : data.applicationCategory,
		primaryPropertyStep: overlayPropertyStep(
			data.primaryPropertyStep,
			propertyKind,
		),
		householdPropertyStep: overlayPropertyStep(
			data.householdPropertyStep,
			propertyKind,
		),
	}
}

/** Apply demo-form dummy sections onto a cloned fixture report. */
export function overlayDemoFormFields(
	report: OverlayReport,
	variant: string,
): OverlayReport {
	const kind = overlayKindFor(variant)
	if (report.kind === 'detail') {
		return { kind: 'detail', data: overlayDetail(report.data, kind) }
	}
	return { kind: 'combined', data: overlayCombined(report.data, kind) }
}
